import classNames from 'classnames';
import React, { memo, useMemo, useState } from 'react';
import { ColorType } from '../../types/component.type';
import { Icon, IconType } from '../icon/icon.component';
import { Text } from '../text/text.component';
import './tab.component.scss';
import { useTranslation } from '@gimlite/common';
import { observer } from 'mobx-react-lite';
import { Count } from '../count/count.component';

export declare namespace TabType {
  type Props = {
    className?: string | null;
    counter?: number | null;
    onClick?: () => void;
    icon?: IconType.Props['variant'] | null;
    iconUnselectedColor?: ColorType | null;
    selected?: boolean | null;
    isSelectable?: boolean | null;
    disabled?: boolean | null;
    children?: React.ReactNode | null;
    enableArrow?: boolean | null;
  };
}

export const Tab = memo(
  ({
    className,
    counter,
    onClick,
    icon,
    selected,
    disabled,
    children,
    isSelectable = true,
    enableArrow = false,
    iconUnselectedColor,
  }: TabType.Props) => {
    return (
      <div
        onClick={() => {
          if (disabled || !isSelectable) return;
          onClick?.();
        }}
        className={classNames('tab', className, {
          'tab--selected': selected,
          'tab--disabled': disabled,
          'tab--selectable': isSelectable,
          'tab--arrow': enableArrow,
        })}
      >
        {icon && (
          <Icon
            variant={icon}
            color={
              selected ? 'black_2' : iconUnselectedColor || 'deep_indigo_70'
            }
          />
        )}
        {children && (
          <Text
            variant="subhead"
            color={selected ? 'black_2' : 'deep_indigo_70'}
            whiteSpace="nowrap"
          >
            {children}
          </Text>
        )}
        {typeof counter === 'number' && (
          <Count disabled={disabled} selected={selected}>
            {counter}
          </Count>
        )}
      </div>
    );
  },
);

export declare namespace TabGroupType {
  type Props = {
    className?: string | null;
    beforeMoreMax?: number;
    afterMoreMax?: number;
    flexWrap?: 'wrap' | 'nowrap';
    children?:
      | React.ReactElement<TabType.Props>
      | React.ReactElement<TabType.Props>[]
      | null;
  };
}

export const TabGroup = memo(
  observer(
    ({
      className,
      children = [],
      beforeMoreMax = 6,
      afterMoreMax = 15,
      flexWrap = 'nowrap',
    }: TabGroupType.Props) => {
      const [displayOther, setDisplayOther] = useState<boolean>(false);
      const { t } = useTranslation();

      const childrenFormatted = useMemo(() => {
        if (!displayOther)
          return React.Children.toArray(children).slice(0, beforeMoreMax);
        return React.Children.toArray(children);
      }, [displayOther, beforeMoreMax, children]);

      return (
        <div
          className={classNames('tabGroup', className)}
          style={{ flexWrap: flexWrap }}
        >
          {childrenFormatted.slice(0, afterMoreMax)}
          {!displayOther && React.Children.count(children) > beforeMoreMax && (
            <Tab onClick={() => setDisplayOther(true)}>
              {t('watermelon-more')}
            </Tab>
          )}
        </div>
      );
    },
  ),
);
